import { makeStyles } from "tss-react/mui";
import { MiButton, MiDetailFields } from "@miplatform/components";
import {
  BUTTON_VARIANTS,
  INPUT_TYPES,
  TYPOGRAPHY_VARIANTS,
} from "@miplatform/constants";
import { Box, Typography } from "@mui/material";
import { ACCOUNT_PRICING_VALUES } from "../../../constants";

export function PriceDistributionTab({
  setCalculationType,
  selectedCategory,
  accountDetail,
  getValues,
  setValue,
  control,
}) {
  const { classes } = useStyles();
  const { draws, calculationType } = getValues();

  const isDollarCalculation = calculationType === ACCOUNT_PRICING_VALUES.DOLLAR;

  const handleEvenDistribution = () => {
    const updatedDrawsDistribution = draws?.map((draw) => ({
      ...draw,
      percentage: parseFloat((100 / draws.length).toFixed(2)),
    }));
    setValue("draws", updatedDrawsDistribution, {
      shouldValidate: true,
      shouldDirty: true,
    });
    handleCalculateTotal(updatedDrawsDistribution);
  };

  const handleCalculateTotal = (updatedDraws) => {
    const total = parseFloat(
      updatedDraws
        .reduce((acc, d) => {
          return acc + (d.percentage ? d.percentage : 0);
        }, 0)
        .toFixed(2)
    );
    setValue("drawTotal", total, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const startingPriceTypeField = {
    name: "startingPriceType",
    formControl: control,
    fieldType: INPUT_TYPES.RADIO_GROUP,
    options: [
      { label: "List Price", value: ACCOUNT_PRICING_VALUES.LIST_PRICE },
      { label: "Cost", value: ACCOUNT_PRICING_VALUES.COST, disabled: true },
    ],
    setValue: (e) => {
      setValue("startingPriceType", e.target.value, {
        shouldValidate: true,
        shouldDirty: true,
      });
    },
  };

  const calculationTypeField = {
    name: "calculationType",
    formControl: control,
    fieldType: INPUT_TYPES.RADIO_GROUP,
    options: [
      { label: "Dollar Modifier ($)", value: ACCOUNT_PRICING_VALUES.DOLLAR },
      {
        label: "Percentage Modifier (%)",
        value: ACCOUNT_PRICING_VALUES.PERCENT,
      },
    ],
    setValue: (e) => {
      setCalculationType(e.target.value);
      setValue("calculationType", e.target.value, {
        shouldValidate: true,
        shouldDirty: true,
      });
      // reset amount when modifiying calculation type
      setValue("amount", 0.0, { shouldValidate: true, shouldDirty: true });
    },
  };

  const amountField = {
    name: "amount",
    formControl: control,
    fieldType: INPUT_TYPES.NUMERIC,
    label: `Markup (${isDollarCalculation ? "$" : "%"})`,
    suffix: `${isDollarCalculation ? "" : "%"}`,
    value: getValues("amount"),
    setValue: (floatValue) => {
      setValue("amount", floatValue, {
        shouldValidate: true,
        shouldDirty: true,
      });
    },
    thousandSeparator: true,
    decimalScale: 2,
    fixedDecimalScale: isDollarCalculation,
    allowNegative: true,
    allowLeadingZeros: false,
    prefix: isDollarCalculation ? "$" : "",
    required: true,
    width: 170,
    flexGrow: 0,
    padding: "8px 0 0 0",
  };

  const drawsFields = draws?.map((draw, index) => ({
    name: `draws[${index}].percentage`,
    formControl: control,
    fieldType: INPUT_TYPES.NUMERIC,
    label: draw.name,
    value: draw.percentage,
    setValue: (floatValue) => {
      setValue(`draws[${index}].percentage`, floatValue, {
        shouldValidate: true,
      });
      handleCalculateTotal(getValues("draws"));
    },
    thousandSeparator: true,
    decimalScale: 2,
    allowNegative: false,
    allowLeadingZeros: false,
    suffix: "%",
    required: true,
    flexGrow: 0,
  }));

  const totalField = {
    name: "drawTotal",
    formControl: control,
    fieldType: INPUT_TYPES.NUMERIC,
    label: "Total Distribution",
    value: getValues("drawTotal"),
    setValue: (floatValue) => {
      setValue("drawTotal", floatValue);
    },
    thousandSeparator: true,
    decimalScale: 2,
    allowNegative: false,
    suffix: "%",
    required: true,
    readOnly: true,
    width: 200,
  };

  return (
    <Box className={classes.container}>
      <Box className={classes.priceBasisModifierContainer}>
        <Box>
          <Typography fontWeight={600} variant={TYPOGRAPHY_VARIANTS.CAPTION}>
            Price Basis
          </Typography>
          <Box>
            <Typography variant={TYPOGRAPHY_VARIANTS.CAPTION}>
              Select the
            </Typography>
            <Typography
              variant={TYPOGRAPHY_VARIANTS.CAPTION}
              className={classes.highlightText}
            >
              {` ${selectedCategory?.categoryName} `}
            </Typography>
            <Typography variant={TYPOGRAPHY_VARIANTS.CAPTION}>
              pricing method and value for
            </Typography>
            <Typography
              variant={TYPOGRAPHY_VARIANTS.CAPTION}
              className={classes.highlightText}
            >
              {` ${accountDetail?.name}. `}
            </Typography>
            <Typography variant={TYPOGRAPHY_VARIANTS.CAPTION}>
              This is the item value to modify when pricing this customer.
            </Typography>
          </Box>
          <MiDetailFields detailFields={[startingPriceTypeField]} />
        </Box>
        <Box>
          <Typography fontWeight={600} variant={TYPOGRAPHY_VARIANTS.CAPTION}>
            Price Modifier
          </Typography>
          <Box>
            <Typography variant={TYPOGRAPHY_VARIANTS.CAPTION}>
              Enter the Price Modifier after selecting your Price Basis to
              calculate prices for
            </Typography>
            <Typography
              variant={TYPOGRAPHY_VARIANTS.CAPTION}
              className={classes.highlightText}
            >
              {` ${accountDetail?.name}.`}
            </Typography>
          </Box>
          <MiDetailFields detailFields={[calculationTypeField]} />
          <MiDetailFields detailFields={[amountField]} />
        </Box>
      </Box>
      <Box className={classes.priceDistributionContainer}>
        <Box>
          <Typography fontWeight={600} variant={TYPOGRAPHY_VARIANTS.CAPTION}>
            Category Pricing Distribution
          </Typography>
          <Box>
            <Typography variant={TYPOGRAPHY_VARIANTS.CAPTION}>
              Enter the
            </Typography>
            <Typography
              variant={TYPOGRAPHY_VARIANTS.CAPTION}
              className={classes.highlightText}
            >
              {` ${selectedCategory?.categoryName} `}
            </Typography>
            <Typography variant={TYPOGRAPHY_VARIANTS.CAPTION}>
              pricing distribution for
            </Typography>
            <Typography
              variant={TYPOGRAPHY_VARIANTS.CAPTION}
              className={classes.highlightText}
            >
              {` ${accountDetail?.name}.`}
            </Typography>
          </Box>
        </Box>
        <Box>
          <MiButton
            title="Distribute Evenly"
            variant={BUTTON_VARIANTS.OUTLINED}
            size="small"
            onClick={() => {
              handleEvenDistribution();
            }}
          />
        </Box>
      </Box>
      <Box display="flex" gap={2} mt={4}>
        <MiDetailFields detailFields={drawsFields} />
        <MiDetailFields detailFields={[totalField]} />
      </Box>
    </Box>
  );
}

const useStyles = makeStyles()((theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
  },
  priceBasisModifierContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    padding: theme.spacing(2),
  },
  highlightText: {
    fontWeight: 600,
    color: theme.palette.primary.main,
  },
  priceDistributionContainer: {
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    padding: theme.spacing(2),
  },
  markupInput: {
    width: 150,
  },
}));
